import React, { useEffect, useRef } from 'react';
import { ThemeProvider } from 'styled-components';
import { showingTimePlusTheme, zillowTheme } from 'zdocs-web-core/theme/Theme';
import { createContextHelper } from 'zdocs-web-core/lib/utils/context-utils';

export enum BrandType {
  ZILLOW = 'Z',
  SHOWING_TIME_PLUS = 'STP',
}

export type BrandContextType = {
  type: BrandType;
  name: string;
  assetBasePath: string;
};

type BrandOptionsType = {
  theme: (theme: any) => any;
  favicon: string;
} & BrandContextType;

const useBrand = () => {
  const brandRef = useRef<BrandOptionsType>();

  if (!brandRef.current) {
    const queryParams = new URLSearchParams(window.location.search);
    const brandParam = queryParams.get('brand') as BrandType;
    const isValidBrand = Object.values(BrandType).includes(brandParam);
    const type: BrandType = isValidBrand ? brandParam : BrandType.ZILLOW;
    const isShowingTimePlus = type === BrandType.SHOWING_TIME_PLUS;
    const name = isShowingTimePlus ? 'ShowingTime+' : 'Zillow';
    const theme = isShowingTimePlus ? showingTimePlusTheme : zillowTheme;
    const favicon = isShowingTimePlus ? 'favicon.png' : 'favicon.ico';
    const urlPathParts = window.location.pathname.split('/');
    const lastUrlPathPart = urlPathParts[urlPathParts.length - 1];
    const appBasePath = lastUrlPathPart === '' ? '' : `${lastUrlPathPart}/`;
    const assetBasePath = `${appBasePath}${isShowingTimePlus ? 'stp/' : ''}`;

    brandRef.current = { type, name, theme, favicon, assetBasePath };
  }

  return brandRef.current;
};

const useWindowTitle = (brandName: string) => {
  useEffect(() => {
    document.title = `${brandName} Docs`;
  }, [brandName]);
};

const useFavicon = (assetBasePath: string, filename: string) => {
  useEffect(() => {
    let favicon = document.querySelector<HTMLLinkElement>('[rel*="icon"]');

    if (!favicon) {
      favicon = document.createElement('link');
      favicon.rel = 'shortcut icon';
      document.head.appendChild(favicon);
    }

    favicon.href = `${assetBasePath}${filename}`;
  }, [assetBasePath, filename]);
};

export const [useBrandContext, BrandContext] =
  createContextHelper<BrandContextType>();

type Props = {
  children: React.ReactNode;
};

export default function BrandProvider({ children }: Props) {
  const brand = useBrand();

  useWindowTitle(brand.name);
  useFavicon(brand.assetBasePath, brand.favicon);

  return (
    <ThemeProvider theme={brand.theme}>
      <BrandContext value={brand}>{children}</BrandContext>
    </ThemeProvider>
  );
}
